// src/components/MonthlyReportGenerator.jsx
// AI-powered monthly facility report generator with Excel export
// 
// INSTALLATION: npm install xlsx

import React, { useState } from 'react';
import { FileSpreadsheet, FileText, Download, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  collectMonthlyCapacityData,
  collectHeatmapData,
  collectAnalyticsData,
  generateCapacityExcelData,
  generateReportDataSummary,
  generateAIReportPrompt
} from '../utils/ReportExportUtils';

// Spinner component
const Spinner = ({ size = 18 }) => (
  <div style={{
    width: size,
    height: size,
    border: '2px solid currentColor',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }} />
);

const MonthlyReportGenerator = ({
  clubId,
  clubName,
  pitches,
  pitchConfigurations,
  teamsList,
  onClose,
  // Optional: pass existing data to avoid re-fetching
  existingCapacityData = null,
  existingHeatmapData = null,
  existingAnalyticsData = null
}) => {
  // State
  const [isCollectingData, setIsCollectingData] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [dataCollected, setDataCollected] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');
  
  // Collected data
  const [capacityData, setCapacityData] = useState(existingCapacityData);
  const [heatmapData, setHeatmapData] = useState(existingHeatmapData);
  const [analyticsData, setAnalyticsData] = useState(existingAnalyticsData);
  
  // Generated report
  const [generatedReport, setGeneratedReport] = useState(null);
  
  // Target month (current month by default)
  const [targetMonth, setTargetMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // ============================================
  // DATA COLLECTION
  // ============================================
  
  const collectAllData = async () => {
    setIsCollectingData(true);
    setError(null);
    setProgress('Starting data collection...');
    
    try {
      // Collect capacity data
      setProgress('Collecting weekly capacity data...');
      const capacity = await collectMonthlyCapacityData(clubId, pitches, pitchConfigurations, targetMonth);
      setCapacityData(capacity);
      
      // Collect heatmap data
      setProgress('Collecting heatmap data...');
      const heatmap = await collectHeatmapData(clubId, pitches, pitchConfigurations, targetMonth);
      setHeatmapData(heatmap);
      
      // Collect analytics data
      setProgress('Collecting team analytics...');
      const analytics = await collectAnalyticsData(clubId, pitches, teamsList, targetMonth);
      setAnalyticsData(analytics);
      
      setDataCollected(true);
      setProgress('Data collection complete!');
    } catch (err) {
      console.error('Error collecting data:', err);
      setError(`Failed to collect data: ${err.message}`);
    } finally {
      setIsCollectingData(false);
    }
  };

  // ============================================
  // EXCEL EXPORT
  // ============================================
  
  const exportToExcel = async () => {
    if (!dataCollected && !capacityData && !heatmapData) {
      setError('Please collect data first before exporting.');
      return;
    }
    
    setIsExporting(true);
    setError(null);
    
    try {
      const excelData = generateCapacityExcelData(capacityData, heatmapData, clubName);
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Add each sheet
      excelData.sheets.forEach(sheet => {
        const ws = XLSX.utils.aoa_to_array ? 
          XLSX.utils.aoa_to_sheet(sheet.data) : 
          XLSX.utils.aoa_to_sheet(sheet.data);
        
        // Set column widths
        const colWidths = sheet.data[0]?.map((_, i) => {
          const maxLen = Math.max(...sheet.data.map(row => String(row[i] || '').length));
          return { wch: Math.min(Math.max(maxLen, 10), 30) };
        }) || [];
        ws['!cols'] = colWidths;
        
        XLSX.utils.book_append_sheet(wb, ws, sheet.name);
      });
      
      // Add analytics sheet if available
      if (analyticsData) {
        const analyticsRows = [
          ['Team Analytics Report', '', '', '', ''],
          [`Month: ${analyticsData.month}`, '', '', '', ''],
          [''],
          ['Team', 'Total Hours', 'Allocations', 'Days Active', 'Avg Min/Day']
        ];
        
        Object.entries(analyticsData.teamAnalytics).forEach(([teamName, data]) => {
          analyticsRows.push([
            teamName,
            data.totalHours,
            data.allocations,
            data.daysActive,
            data.avgMinutesPerDay
          ]);
        });
        
        const analyticsWs = XLSX.utils.aoa_to_sheet(analyticsRows);
        analyticsWs['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, analyticsWs, 'Team Analytics');
      }
      
      // Generate and download
      XLSX.writeFile(wb, excelData.filename);
      
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError(`Failed to export: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // ============================================
  // AI REPORT GENERATION
  // ============================================
  
  const generateAIReport = async () => {
    if (!dataCollected && !capacityData && !heatmapData && !analyticsData) {
      setError('Please collect data first before generating a report.');
      return;
    }
    
    setIsGeneratingReport(true);
    setError(null);
    setProgress('Preparing compact data summary...');
    
    try {
      // Create a COMPACT data summary optimised for fast AI processing
      // This is the key to working within 10 second timeout
      const compactSummary = createCompactDataSummary();
      
      console.log(`Compact summary size: ${JSON.stringify(compactSummary).length} chars`);
      
      setProgress('Generating AI report...');
      
      // Call serverless function with timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000); // 12 second client timeout
      
      try {
        const response = await fetch('/.netlify/functions/generate-report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ dataSummary: compactSummary }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          
          // Handle specific error cases
          if (response.status === 504) {
            throw new Error('Report generation timed out. Please try again.');
          }
          
          throw new Error(errorData.error || `Request failed: ${response.status}`);
        }
        
        const data = await response.json();
        const reportContent = data.content[0].text;
        
        setGeneratedReport({
          content: reportContent,
          generatedAt: new Date().toISOString(),
          dataSummary: compactSummary
        });
        
        setProgress('Report generated successfully!');
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out. Please try again - the AI service may be busy.');
        }
        throw fetchError;
      }
      
    } catch (err) {
      console.error('Error generating AI report:', err);
      setError(`Failed to generate report: ${err.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Create a COMPACT data summary - only essential metrics
  // This is crucial for staying within the 10 second free tier timeout
  const createCompactDataSummary = () => {
    const summary = {
      clubName: clubName || 'Club',
      month: targetMonth,
      summary: {
        totalHours: 0,
        totalAllocations: 0,
        totalTeams: 0,
        activeDays: 0,
        avgUtilization: 0
      },
      topTeams: [],
      pitchUtilization: {},
      peakTimes: [],
      lowTimes: []
    };

    // Extract summary from analytics data
    if (analyticsData?.summary) {
      summary.summary = {
        totalHours: Math.round((analyticsData.summary.totalHours || 0) * 10) / 10,
        totalAllocations: analyticsData.summary.totalAllocations || 0,
        totalTeams: analyticsData.summary.totalTeams || 0,
        activeDays: analyticsData.summary.activeDays || 0
      };
    }

    // Get top teams by hours (max 5)
    if (analyticsData?.teamAnalytics) {
      const teamEntries = Object.entries(analyticsData.teamAnalytics)
        .map(([name, data]) => ({
          name,
          hours: Math.round((data.totalHours || 0) * 10) / 10,
          allocations: data.allocations || 0,
          daysActive: data.daysActive || 0
        }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 5);
      
      summary.topTeams = teamEntries;
    }

    // Get peak/low times if available
    if (analyticsData?.peakTimes) {
      summary.peakTimes = analyticsData.peakTimes.slice(0, 3);
    }
    if (analyticsData?.lowTimes) {
      summary.lowTimes = analyticsData.lowTimes.slice(0, 3);
    }

    // Get pitch utilization from capacity data
    // Structure: capacityData.weeks[].pitchData[pitchId].days[dayName].total
    if (capacityData?.weeks && capacityData.weeks.length > 0) {
      const pitchTotals = {};
      const pitchCounts = {};
      
      capacityData.weeks.forEach(week => {
        if (week.pitchData) {
          Object.entries(week.pitchData).forEach(([pitchId, pitchInfo]) => {
            if (pitchInfo.days) {
              Object.values(pitchInfo.days).forEach(dayData => {
                const util = dayData.total || 0;
                pitchTotals[pitchId] = (pitchTotals[pitchId] || 0) + util;
                pitchCounts[pitchId] = (pitchCounts[pitchId] || 0) + 1;
              });
            }
          });
        }
      });
      
      Object.keys(pitchTotals).forEach(pitchId => {
        summary.pitchUtilization[pitchId] = Math.round(pitchTotals[pitchId] / pitchCounts[pitchId]);
      });

      // Calculate overall average utilization
      const allUtils = Object.values(summary.pitchUtilization);
      if (allUtils.length > 0) {
        summary.summary.avgUtilization = Math.round(allUtils.reduce((a, b) => a + b, 0) / allUtils.length);
      }
    }

    // Log for debugging
    console.log('Compact summary created:', JSON.stringify(summary, null, 2));

    return summary;
  };

  // ============================================
  // EXPORT REPORT AS DOCUMENT
  // ============================================
  
  const downloadReportAsText = () => {
    if (!generatedReport) return;
    
    const reportText = `
================================================================================
MONTHLY FACILITY UTILIZATION REPORT
================================================================================
Club: ${clubName}
Period: ${capacityData?.month || analyticsData?.month || 'Current Month'}
Generated: ${new Date().toLocaleDateString()}
================================================================================

${generatedReport.content}

================================================================================
END OF REPORT
================================================================================
    `.trim();
    
    const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Monthly_Report_${targetMonth}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // ============================================
  // RENDER
  // ============================================
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              Monthly Report Generator
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
              Export data and generate AI-powered facility reports
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Month Selector */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Report Month
            </label>
            <input
              type="month"
              value={targetMonth}
              onChange={(e) => {
                setTargetMonth(e.target.value);
                setDataCollected(false);
                setGeneratedReport(null);
              }}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                width: '200px'
              }}
            />
          </div>
          
          {/* Error Display */}
          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertCircle size={20} color="#dc2626" />
              <span style={{ color: '#dc2626', fontSize: '14px' }}>{error}</span>
            </div>
          )}
          
          {/* Progress Display */}
          {progress && (isCollectingData || isGeneratingReport) && (
            <div style={{
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Spinner size={20} />
              <span style={{ color: '#2563eb', fontSize: '14px' }}>{progress}</span>
            </div>
          )}
          
          {/* Action Buttons */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {/* Collect Data Button */}
            <button
              onClick={collectAllData}
              disabled={isCollectingData || isGeneratingReport}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '14px 20px',
                backgroundColor: dataCollected ? '#dcfce7' : '#22c55e',
                color: dataCollected ? '#166534' : 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isCollectingData ? 'not-allowed' : 'pointer',
                opacity: isCollectingData ? 0.7 : 1,
                transition: 'all 0.2s'
              }}
            >
              {isCollectingData ? (
                <Spinner size={18} />
              ) : dataCollected ? (
                <CheckCircle size={18} />
              ) : (
                <RefreshCw size={18} />
              )}
              {dataCollected ? 'Data Collected' : 'Collect Data'}
            </button>
            
            {/* Export to Excel Button */}
            <button
              onClick={exportToExcel}
              disabled={!dataCollected || isExporting}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '14px 20px',
                backgroundColor: !dataCollected ? '#e5e7eb' : '#3b82f6',
                color: !dataCollected ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: !dataCollected || isExporting ? 'not-allowed' : 'pointer',
                opacity: isExporting ? 0.7 : 1,
                transition: 'all 0.2s'
              }}
            >
              {isExporting ? (
                <Spinner size={18} />
              ) : (
                <FileSpreadsheet size={18} />
              )}
              Export to Excel
            </button>
            
            {/* Generate AI Report Button */}
            <button
              onClick={generateAIReport}
              disabled={!dataCollected || isGeneratingReport}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '14px 20px',
                backgroundColor: !dataCollected ? '#e5e7eb' : '#8b5cf6',
                color: !dataCollected ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: !dataCollected || isGeneratingReport ? 'not-allowed' : 'pointer',
                opacity: isGeneratingReport ? 0.7 : 1,
                transition: 'all 0.2s'
              }}
            >
              {isGeneratingReport ? (
                <Spinner size={18} />
              ) : (
                <FileText size={18} />
              )}
              Generate AI Report
            </button>
          </div>
          
          {/* Data Summary */}
          {dataCollected && (
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                Data Summary
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px'
              }}>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Weeks</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                    {capacityData?.weeks?.length || 0}
                  </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Pitches</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                    {pitches?.length || 0}
                  </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Teams</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                    {analyticsData?.summary?.totalTeams || 0}
                  </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Hours</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                    {analyticsData?.summary?.totalHours?.toFixed(1) || 0}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Generated Report Display */}
          {generatedReport && (
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                backgroundColor: '#f3f4f6',
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                  Generated Report
                </h3>
                <button
                  onClick={downloadReportAsText}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    backgroundColor: '#374151',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  <Download size={14} />
                  Download
                </button>
              </div>
              <div style={{
                padding: '20px',
                maxHeight: '400px',
                overflowY: 'auto',
                backgroundColor: 'white'
              }}>
                <pre style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#374151'
                }}>
                  {generatedReport.content}
                </pre>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#374151'
            }}
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Spinner Animation CSS */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MonthlyReportGenerator;
